import * as XLSX from "xlsx";
import JSZip from "jszip";
import axios from "axios";
import config from "../../config.json";
import { fetchProducts } from "./MenuService";

function authHeaders() {
  const token = localStorage.getItem("tokenLogin");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function sanitizeSheetName(name) {
  let s = String(name || "Sheet").slice(0, 31);
  s = s.replace(/[:\/\\?\*\[\]]/g, "-");
  return s.trim() || "Sheet";
}

function stripHtml(html) {
  if (html == null || typeof html !== "string") return "";
  const tmp = typeof document !== "undefined" ? document.createElement("div") : null;
  if (tmp) {
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function joinLangDetails(arr, field) {
  if (!Array.isArray(arr)) return "";
  return arr
    .map((d) => {
      const v = d?.[field];
      if (v == null || v === "") return "";
      return `${d.language || "?"}: ${stripHtml(String(v))}`;
    })
    .filter(Boolean)
    .join(" | ");
}

function formatExtraSizes(extraSizes) {
  if (!Array.isArray(extraSizes) || !extraSizes.length) return "";
  return extraSizes
    .map((s) => `${s.size || "?"}=${s.finalPrice ?? ""}`)
    .join("; ");
}

function unwrapProduct(ref) {
  if (!ref) return null;
  if (ref.product) return ref.product;
  return ref;
}

function productIdOf(p) {
  if (!p) return "";
  const id = p._id ?? p;
  return id != null ? String(id) : "";
}

function safeFilenamePart(s) {
  return String(s || "menu")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .slice(0, 80);
}

function safeImageFileSegment(name) {
  return String(name || "file").replace(/[^a-zA-Z0-9._-]+/g, "_");
}

function extensionFromFilename(name) {
  const m = String(name).match(/(\.[a-zA-Z0-9]{2,5})$/);
  return m ? m[1].toLowerCase() : ".webp";
}

/** Full CDN URL for a stored file (menu/foo.webp, category/bar.webp, …). */
function uploadsUrl(relativePath) {
  const rel = String(relativePath || "").replace(/^\/+/, "");
  if (!rel) return "";
  const base = (config.UPLOADS_SERVER || "").replace(/\/+$/, "");
  return `${base}/${rel}`;
}

/**
 * Collect unique image download tasks. `zipFileName` is basename only (no "images/").
 */
class ImageTaskCollector {
  constructor() {
    /** @type {Set<string>} */
    this.seenUrls = new Set();
    /** @type {Set<string>} */
    this.usedZipNames = new Set();
    /** @type {{ url: string, zipFileName: string, entityType: string, entityId: string, sourceFile: string }[]} */
    this.tasks = [];
  }

  add(relativeUploadPath, entityType, entityId, preferredBase) {
    if (!relativeUploadPath) return;
    const url = uploadsUrl(relativeUploadPath);
    if (!url || this.seenUrls.has(url)) return;
    this.seenUrls.add(url);

    const sourceFile = relativeUploadPath.split("/").pop() || "file";
    const ext = extensionFromFilename(sourceFile);
    const base =
      safeImageFileSegment(preferredBase || sourceFile).replace(/\.[a-zA-Z0-9]+$/, "") ||
      "image";

    let zipFileName = `${entityType}_${safeFilenamePart(entityId).slice(0, 24)}__${base}${ext}`;
    let n = 0;
    while (this.usedZipNames.has(zipFileName)) {
      n += 1;
      zipFileName = `${entityType}_${safeFilenamePart(entityId).slice(0, 24)}__${base}_${n}${ext}`;
    }
    this.usedZipNames.add(zipFileName);

    this.tasks.push({
      url,
      zipFileName,
      entityType,
      entityId: String(entityId || ""),
      sourceFile,
    });
  }
}

function buildWorkbookFromRows({
  menuRows,
  categoryRows,
  productRows,
  choiceRows,
  itemRows,
  imageManifestRows,
}) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(menuRows),
    sanitizeSheetName("Menu")
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(categoryRows.length ? categoryRows : [{ note: "No categories" }]),
    sanitizeSheetName("Categories")
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(productRows.length ? productRows : [{ note: "No products in menu" }]),
    sanitizeSheetName("Products")
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(choiceRows.length ? choiceRows : [{ note: "No choices linked" }]),
    sanitizeSheetName("Choices")
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(itemRows.length ? itemRows : [{ note: "No items in choices" }]),
    sanitizeSheetName("Items")
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(
      imageManifestRows.length ? imageManifestRows : [{ note: "No images referenced" }]
    ),
    sanitizeSheetName("ImageFiles")
  );
  return wb;
}

/**
 * Load menu + related data and build all export rows + image task list.
 */
async function loadMenuExportBundle(menuId, menuDisplayName) {
  const base = config.dev;
  const menuUrl = `${base}/v2/menu/getByID/${menuId}`;

  const [menuRes, productsRaw, choicesRes, itemsRes] = await Promise.all([
    axios.get(menuUrl, { headers: authHeaders() }),
    fetchProducts(),
    axios.get(`${base}/v2/choices/retrieve`, { headers: authHeaders() }),
    axios.get(`${base}/v2/items/retrieve`, { headers: authHeaders() }),
  ]);

  const menu = menuRes.data;
  const productsList = Array.isArray(productsRaw) ? productsRaw : [];
  const choicesList = Array.isArray(choicesRes.data) ? choicesRes.data : [];
  const itemsList = Array.isArray(itemsRes.data) ? itemsRes.data : [];

  const byProductId = new Map();
  for (const p of productsList) {
    if (p?._id) byProductId.set(String(p._id), p);
  }
  const byChoiceId = new Map();
  for (const c of choicesList) {
    if (c?._id) byChoiceId.set(String(c._id), c);
  }
  const byItemId = new Map();
  for (const it of itemsList) {
    if (it?._id) byItemId.set(String(it._id), it);
  }

  const menuNameEn =
    menu?.menuDetails?.find((d) => d.language === "en")?.menuName ||
    menu?.menuDetails?.[0]?.menuName ||
    menuDisplayName;

  const images = new ImageTaskCollector();
  if (menu.photo) {
    images.add(`menu/${menu.photo}`, "menu", String(menu._id), menu.photo);
  }

  const menuRows = [
    {
      menuId: String(menu._id),
      menuNameEN: menuNameEn,
      menuNamesAllLangs: joinLangDetails(menu.menuDetails, "menuName"),
      photo: menu.photo || "",
      photoUrl: menu.photo ? uploadsUrl(`menu/${menu.photo}`) : "",
      isEnabled: menu.isEnabled !== false,
      displayOrder: menu.displayOrder ?? "",
      start_date: menu.start_date ? String(menu.start_date) : "",
      end_date: menu.end_date ? String(menu.end_date) : "",
      start_time: menu.start_time || "",
      end_time: menu.end_time || "",
      day_of_week: Array.isArray(menu.day_of_week) ? menu.day_of_week.join(", ") : "",
      isArchived: !!menu.isArchived,
    },
  ];

  const categoryRows = [];
  const productRows = [];
  const choiceIdsUsed = new Set();
  const itemIdsUsed = new Set();

  const categoriesList = Array.isArray(menu.categoriesList) ? menu.categoriesList : [];

  for (const catItem of categoriesList) {
    const cat = catItem.category;
    if (!cat) continue;

    const catTitles = joinLangDetails(cat.categoryDetails, "categoryTitle");
    const catId = String(cat._id || "");
    const catOrder = catItem.order ?? "";

    if (cat.photo) {
      images.add(`category/${cat.photo}`, "category", catId, cat.photo);
    }

    categoryRows.push({
      menuName: menuNameEn,
      categoryOrder: catOrder,
      categoryId: catId,
      categoryTitlesAllLangs: catTitles,
      categoryPhoto: cat.photo || "",
      subcategoryOrder: "",
      subcategoryId: "",
      subcategoryTitlesAllLangs: "",
      placement: "category",
    });

    const directProducts = Array.isArray(catItem.productList) ? catItem.productList : [];
    for (const pl of directProducts) {
      const pop = unwrapProduct(pl.product);
      const pid = productIdOf(pop);
      if (!pid) continue;
      const catalog = byProductId.get(pid) || {};
      const merged = { ...pop, ...catalog };
      const order = pl.order ?? "";

      const choiceRefs = Array.isArray(merged.choices) ? merged.choices : [];
      for (const ch of choiceRefs) {
        const cid = typeof ch === "object" && ch?._id ? String(ch._id) : String(ch);
        if (cid) choiceIdsUsed.add(cid);
      }

      if (Array.isArray(merged.photos)) {
        merged.photos.forEach((fn, idx) => {
          if (fn) images.add(`product/${fn}`, "product", pid, `${pid}_photo_${idx}_${fn}`);
        });
      }

      productRows.push({
        menuName: menuNameEn,
        placement: "Category (direct)",
        categoryOrder: catOrder,
        categoryId: catId,
        categoryTitles: catTitles,
        subcategoryOrder: "",
        subcategoryId: "",
        subcategoryTitles: "",
        productOrder: order,
        productId: pid,
        productTitlesAllLangs: joinLangDetails(merged.productDetails, "productTitle"),
        descriptionsAllLangs: joinLangDetails(merged.productDetails, "productDescription"),
        price: merged.price ?? "",
        rate: merged.rate ?? "",
        haveMultipleSize: !!merged.haveMultipleSize,
        extraSizes: formatExtraSizes(merged.extraSizes),
        tags: Array.isArray(merged.tags) ? merged.tags.join(", ") : "",
        photos: Array.isArray(merged.photos) ? merged.photos.join(", ") : "",
        photoUrls: Array.isArray(merged.photos)
          ? merged.photos.map((fn) => (fn ? uploadsUrl(`product/${fn}`) : "")).filter(Boolean).join(" | ")
          : "",
        preparationTime: merged.preparationTime ?? "",
        choiceIds: choiceRefs
          .map((c) => (typeof c === "object" && c?._id ? String(c._id) : String(c)))
          .filter(Boolean)
          .join(", "),
        choiceTitles: choiceRefs
          .map((c) => {
            const id = typeof c === "object" && c?._id ? String(c._id) : String(c);
            const doc = typeof c === "object" && c?.choiceDetails ? c : byChoiceId.get(id);
            return doc ? joinLangDetails(doc.choiceDetails, "choiceTitle") : id;
          })
          .filter(Boolean)
          .join(" || "),
      });
    }

    const subs = Array.isArray(cat.subcategories) ? cat.subcategories : [];
    for (const subWrap of subs) {
      const sub = subWrap.subCategory;
      if (!sub) continue;
      const subTitles = joinLangDetails(sub.subCategoryDetails, "title");
      const subId = String(sub._id || "");
      const subOrder = subWrap.order ?? "";

      categoryRows.push({
        menuName: menuNameEn,
        categoryOrder: catOrder,
        categoryId: catId,
        categoryTitlesAllLangs: catTitles,
        subcategoryOrder: subOrder,
        subcategoryId: subId,
        subcategoryTitlesAllLangs: subTitles,
        placement: "subcategory",
      });

      const subProducts = Array.isArray(sub.productList) ? sub.productList : [];
      for (const pl of subProducts) {
        const pop = unwrapProduct(pl.product);
        const pid = productIdOf(pop);
        if (!pid) continue;
        const catalog = byProductId.get(pid) || {};
        const merged = { ...pop, ...catalog };
        const order = pl.order ?? "";

        const choiceRefs = Array.isArray(merged.choices) ? merged.choices : [];
        for (const ch of choiceRefs) {
          const cid = typeof ch === "object" && ch?._id ? String(ch._id) : String(ch);
          if (cid) choiceIdsUsed.add(cid);
        }

        if (Array.isArray(merged.photos)) {
          merged.photos.forEach((fn, idx) => {
            if (fn) images.add(`product/${fn}`, "product", pid, `${pid}_photo_${idx}_${fn}`);
          });
        }

        productRows.push({
          menuName: menuNameEn,
          placement: "Subcategory",
          categoryOrder: catOrder,
          categoryId: catId,
          categoryTitles: catTitles,
          subcategoryOrder: subOrder,
          subcategoryId: subId,
          subcategoryTitles: subTitles,
          productOrder: order,
          productId: pid,
          productTitlesAllLangs: joinLangDetails(merged.productDetails, "productTitle"),
          descriptionsAllLangs: joinLangDetails(merged.productDetails, "productDescription"),
          price: merged.price ?? "",
          rate: merged.rate ?? "",
          haveMultipleSize: !!merged.haveMultipleSize,
          extraSizes: formatExtraSizes(merged.extraSizes),
          tags: Array.isArray(merged.tags) ? merged.tags.join(", ") : "",
          photos: Array.isArray(merged.photos) ? merged.photos.join(", ") : "",
          photoUrls: Array.isArray(merged.photos)
            ? merged.photos.map((fn) => (fn ? uploadsUrl(`product/${fn}`) : "")).filter(Boolean).join(" | ")
            : "",
          preparationTime: merged.preparationTime ?? "",
          choiceIds: choiceRefs
            .map((c) => (typeof c === "object" && c?._id ? String(c._id) : String(c)))
            .filter(Boolean)
            .join(", "),
          choiceTitles: choiceRefs
            .map((c) => {
              const id = typeof c === "object" && c?._id ? String(c._id) : String(c);
              const doc = typeof c === "object" && c?.choiceDetails ? c : byChoiceId.get(id);
              return doc ? joinLangDetails(doc.choiceDetails, "choiceTitle") : id;
            })
            .filter(Boolean)
            .join(" || "),
        });
      }
    }
  }

  for (const cid of choiceIdsUsed) {
    const ch = byChoiceId.get(cid);
    if (ch?.items?.length) {
      for (const it of ch.items) {
        const iid = typeof it === "object" && it?._id ? String(it._id) : String(it);
        if (iid) itemIdsUsed.add(iid);
      }
    }
  }

  const choiceRows = [];
  for (const cid of choiceIdsUsed) {
    const ch = byChoiceId.get(cid);
    choiceRows.push({
      choiceId: cid,
      titlesAllLangs: ch ? joinLangDetails(ch.choiceDetails, "choiceTitle") : "",
      minSelected: ch?.minSelected ?? "",
      maxSelected: ch?.maxSelected ?? "",
      obligatory: ch?.obligatory ?? "",
      isFree: ch?.isFree ?? "",
      isArchived: ch?.isArchived ?? "",
      itemIds: Array.isArray(ch?.items)
        ? ch.items.map((it) => (typeof it === "object" && it?._id ? String(it._id) : String(it))).join(", ")
        : "",
    });
  }

  const itemRows = [];
  for (const iid of itemIdsUsed) {
    const it = byItemId.get(iid);
    if (it?.photo) {
      images.add(`product/${it.photo}`, "item", iid, it.photo);
    }
    itemRows.push({
      itemId: iid,
      titlesAllLangs: it ? joinLangDetails(it.itemDetails, "itemTitle") : "",
      photo: it?.photo || "",
      photoUrl: it?.photo ? uploadsUrl(`product/${it.photo}`) : "",
      price: it?.price ?? "",
      havePrice: it?.havePrice ?? "",
      isArchived: it?.isArchived ?? "",
    });
  }

  const imageManifestRows = images.tasks.map((t) => ({
    zipPath: `images/${t.zipFileName}`,
    sourceUrl: t.url,
    entityType: t.entityType,
    entityId: t.entityId,
    sourceFile: t.sourceFile,
    downloadStatus: "pending",
  }));

  return {
    menuNameEn,
    menuRows,
    categoryRows,
    productRows,
    choiceRows,
    itemRows,
    imageManifestRows,
    imageTasks: images.tasks,
  };
}

async function fetchImageBlob(url) {
  const { data } = await axios.get(url, {
    responseType: "blob",
    timeout: 120000,
  });
  return data;
}

function downloadBlob(blob, filename) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

/**
 * Excel only (no images). Kept for compatibility.
 */
export async function exportMenuToExcel(menuId, menuDisplayName = "menu") {
  const bundle = await loadMenuExportBundle(menuId, menuDisplayName);
  const { menuNameEn, imageTasks, ...rowBundle } = bundle;
  void imageTasks;
  const wb = buildWorkbookFromRows(rowBundle);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const fname = `menu-export-${safeFilenamePart(bundle.menuNameEn)}-${stamp}.xlsx`;
  XLSX.writeFile(wb, fname);
}

/**
 * Full backup: ZIP with `{MenuName}_menu_backup.xlsx` + `images/` folder (all referenced images).
 */
export async function exportMenuBackupZip(menuId, menuDisplayName = "menu") {
  const bundle = await loadMenuExportBundle(menuId, menuDisplayName);
  const { menuNameEn, imageTasks, ...rowBundle } = bundle;

  const failedLines = [];
  const manifestFinal = rowBundle.imageManifestRows.map((r) => ({ ...r }));

  const zip = new JSZip();
  const imgFolder = zip.folder("images");

  const concurrency = 5;
  for (let i = 0; i < imageTasks.length; i += concurrency) {
    const chunk = imageTasks.slice(i, i + concurrency);
    await Promise.all(
      chunk.map(async (task) => {
        const manifestRow = manifestFinal.find(
          (m) => m.zipPath === `images/${task.zipFileName}`
        );
        try {
          const blob = await fetchImageBlob(task.url);
          imgFolder.file(task.zipFileName, blob);
          if (manifestRow) manifestRow.downloadStatus = "ok";
        } catch (e) {
          const msg = `${task.url} → ${e?.message || e}`;
          failedLines.push(msg);
          if (manifestRow) manifestRow.downloadStatus = `failed: ${e?.message || String(e)}`;
        }
      })
    );
  }

  if (failedLines.length) {
    zip.file(
      "images/_FAILED_DOWNLOADS.txt",
      `Some images could not be downloaded (often CORS or network). Re-download manually using URLs in the Excel sheet "ImageFiles".\n\n${failedLines.join("\n")}`
    );
  }

  const wb = buildWorkbookFromRows({ ...rowBundle, imageManifestRows: manifestFinal });
  const xlsxArray = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const excelName = `${safeFilenamePart(menuNameEn)}_menu_backup.xlsx`;
  zip.file(excelName, xlsxArray);

  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const zipName = `menu-backup-${safeFilenamePart(menuNameEn)}-${stamp}.zip`;
  const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  downloadBlob(zipBlob, zipName);
}
